import { useEffect, useRef, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useShellActions, useShellState } from './shell-context';
import { ROLE_LABEL, TABS, backPathOf, rootPathOfSection, roleLabelFor, sectionOfPath } from './nav';
import { useMeContext } from './MeContext';
import { isTelegram, showBackButton } from '../lib/telegram';
import { setOwnerTenant } from '../api/auth';
import { DevRoleSwitcher } from './DevRoleSwitcher';
import { SwapIcon } from '../ui/icons';

/**
 * Оболочка приложения: шапка, полотно экрана, главная кнопка или вкладки,
 * шторка и тост. Разметка и классы — из прототипа (`.stage` → `.phone`).
 */
export function Frame() {
  const me = useMeContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const state = useShellState();
  const { closeSheet, askBeforeLeaving } = useShellActions();

  /**
   * Шторка одна на всё приложение — меняется только её содержимое, а прокрутка
   * остаётся от прежнего. Шторка, открытая из другой (карточка → правка),
   * появлялась прокрученной до середины формы, мимо заголовка. Возвращаем к
   * началу при каждой смене содержимого.
   */
  const sheetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (state.sheet) sheetRef.current?.scrollTo({ top: 0 });
  }, [state.sheet]);

  const section = sectionOfPath(pathname, me);
  // Корень берём у текущего раздела, а не у роли: совмещающий роли ходит по
  // обоим, и «назад» должно возвращать туда, откуда пришли. Подэкрану карточки
  // организации возврат нужен на уровень выше корня — см. backPathOf.
  const root = rootPathOfSection(section);
  const back = backPathOf(pathname, section);
  const tabs = section === 'client' || section === 'nb' ? null : TABS[section];
  // Активна самая длинная подходящая вкладка: у «/staff» и «/staff/kids» общий
  // префикс, и без этого подэкран подсвечивал бы корневую вкладку.
  const activeTab = tabs
    ? tabs
        .filter((t) => pathname === t.path || pathname.startsWith(`${t.path}/`))
        .sort((a, b) => b.path.length - a.path.length)[0]?.path ?? null
    : null;
  const atRoot = pathname === root || (tabs?.some((t) => t.path === pathname) ?? false);
  const canGoBack = !atRoot;

  /**
   * Администратор со своей карточкой специалиста ведёт и приёмы, и организацию.
   * Маршруты обоих разделов ему зарегистрированы (см. routerFor), входит он в
   * рабочее место (см. rootPathFor), а в админку переходит сам. Подпись роли в
   * шапке и была тем местом, которое отвечает на вопрос «где я»; она же и
   * переключает.
   */
  const dualRole = !me.ownerVisit && !!me.isAdmin && !!me.specialistId
    && (section === 'admin' || section === 'spec');
  const otherRole = section === 'admin' ? 'spec' : 'admin';
  const roleLabel = roleLabelFor(me, section);

  const switchRole = () => {
    // Через ту же проверку, что и кнопка «назад»: уйти из недосохранённой формы
    // в другой раздел ничем не лучше, чем уйти из неё назад.
    void askBeforeLeaving().then((ok) => {
      if (ok) navigate(otherRole === 'admin' ? '/admin' : '/staff');
    });
  };

  const goBack = () => {
    void askBeforeLeaving().then((ok) => {
      if (!ok) return;
      // Из подэкрана возвращаемся в корень раздела, если истории нет: открытая
      // по прямой ссылке страница не должна выкидывать из приложения.
      if (window.history.state?.idx > 0) navigate(-1);
      else navigate(back);
    });
  };

  // Переключение вкладки — такой же уход с экрана, как «назад»: форма с
  // несохранёнными правками успевает спросить, прежде чем её снесут.
  const goTab = (path: string) => {
    if (path === pathname) return;
    void askBeforeLeaving().then((ok) => ok && navigate(path));
  };

  // Кнопка «назад» в чате Telegram — та же навигация, что и стрелка в шапке.
  useEffect(() => {
    if (!canGoBack) return;
    return showBackButton(goBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canGoBack, pathname, back]);

  return (
    <div className={`stage${import.meta.env.DEV ? ' has-devbar' : ''}`}>
      {import.meta.env.DEV ? <DevRoleSwitcher /> : null}

      <div className="phone">
        <header className="topbar">
          {/* В Telegram роль стрелки играет системная кнопка «назад»: две стрелки подряд — шум. */}
          <button className="back" hidden={!canGoBack || isTelegram()} aria-label="Назад" onClick={goBack}>
            <Chevron />
          </button>
          {me.center.logo ? <img className="brandmark" src={me.center.logo} alt="" /> : null}
          <h1>{state.title}</h1>
          {dualRole ? (
            <button
              className="role role-switch"
              type="button"
              onClick={switchRole}
              aria-label={`Сейчас ${roleLabel}. Переключиться: ${ROLE_LABEL[otherRole]}`}
            >
              {roleLabel}
              <SwapIcon />
            </button>
          ) : (
            <span className="role">{roleLabel}</span>
          )}
        </header>

        {me.ownerVisit ? <OwnerVisitBar /> : null}
        {me.readOnly ? <ReadOnlyBar /> : null}

        <div className="screenwrap" key={pathname}>
          <Outlet />
        </div>

        {tabs ? (
          <nav className="tabbar">
            {tabs.map((t) => (
              <button
                key={t.path}
                type="button"
                aria-pressed={activeTab === t.path}
                onClick={() => goTab(t.path)}
              >
                <TabIcon path={t.icon} />
                <span>{t.label}</span>
              </button>
            ))}
          </nav>
        ) : (
          <div className="mainbtn-slot" hidden={!state.main}>
            <button
              className="mainbtn"
              disabled={state.main?.disabled}
              onClick={() => state.main?.onClick()}
            >
              {state.main?.label ?? ''}
            </button>
          </div>
        )}

        <div
          className={`sheet-back${state.sheet ? ' is-open' : ''}`}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeSheet();
          }}
        >
          <div className="sheet" role="dialog" aria-modal="true" ref={sheetRef}>
            {state.sheet}
          </div>
        </div>

        <div className={`toast${state.toast ? ' is-on' : ''}`}>
          {state.toast?.text}
          {state.toast?.action ? (
            <button type="button" className="toast-act" onClick={state.toast.action.onClick}>
              {state.toast.action.label}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Владелец сервиса работает в чужой организации.
 *
 * Полоса висит на всех её экранах, а не показывается один раз при входе: она
 * напоминает про особый режим в любой момент и про то, что след уже оставлен.
 *
 * Названия организации в полосе нет намеренно: оно и так стоит в карточке
 * организации и в шапке логотипом, а в узкой полосе вытесняло собой то
 * единственное, ради чего она висит, — что режим особый и записывается.
 *
 * Кнопка называет то, что делает: она заканчивает режим владельца, а не выходит
 * из приложения. Поведение прежнее — сброс организации и возврат в их список.
 */
function OwnerVisitBar() {
  return (
    <div className="visit-bar">
      <span>Режим владельца сервиса. Действия записываются в журнал.</span>
      <button
        type="button"
        onClick={() => {
          // Роль и набор маршрутов меняются целиком, поэтому не переход, а
          // перезагрузка: половина приложения не должна остаться от визита.
          setOwnerTenant(null);
          window.location.assign('/owner');
        }}
      >
        Выйти из режима
      </button>
    </div>
  );
}

/** Подписка приостановлена: смотреть можно, записываться — нет. */
function ReadOnlyBar() {
  const me = useMeContext();
  return (
    <div className="readonly-bar">
      <span className="ico">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 4l9 16H3z" />
          <path d="M12 10v4M12 17h.01" />
        </svg>
      </span>
      <span>
        Запись временно приостановлена. Уже созданные записи в силе — по вопросам пишите
        администратору: {me.center.adminContact || me.center.phone}
      </span>
    </div>
  );
}

/** Иконки вкладок собираются из тех же строк, что в прототипе. */
export function TabIcon({ path }: { path: string }) {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {path.split('|').map((part, i) =>
        part === 'circle'
          ? <circle key={i} cx="12" cy="12" r="9" />
          : <path key={i} d={part} />,
      )}
    </svg>
  );
}

function Chevron() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

/** Экран прототипа: `<section class="screen is-active">`. */
export function Screen({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={`screen is-active${className ? ` ${className}` : ''}`}>{children}</section>;
}

/**
 * Оболочка без роутера: вход ещё не состоялся, роли нет, вкладок и главной
 * кнопки тоже. Нужна экранам загрузки, ошибки и входа по приглашению.
 */
export function Standalone({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={`stage${import.meta.env.DEV ? ' has-devbar' : ''}`}>
      {import.meta.env.DEV ? <DevRoleSwitcher /> : null}
      <div className="phone">
        <header className="topbar">
          <h1>{title}</h1>
        </header>
        <div className="screenwrap">{children}</div>
      </div>
    </div>
  );
}
